import {
	clearAuthSession,
	hydrateAuthFromStorage,
	isAccessTokenExpiringSoon,
	persistAuthSession,
	readAuthSession,
} from './authSession';

const REFRESH_PATH = '/api/auth/refresh';
const MIGRATE_PATH = '/api/auth/migrate';
const LOCK_STORAGE_KEY = 'grizzly-tg-auth-refresh-lock';
const LOCK_TTL_MS = 15000;

const SKIP_AUTH_RETRY_PATHS = [
	'/api/auth/refresh',
	'/api/auth/migrate',
	'/api/tg-user-auth',
];

function requestPath(config = {}) {
	const url = String(config.url || '');
	try {
		if (/^https?:\/\//i.test(url)) {
			return new URL(url).pathname || url;
		}
	} catch {
		// ignore
	}
	return url.startsWith('/') ? url : `/${url}`;
}

function shouldSkipAuthRetry(config = {}) {
	if (config.__authRetry || config.__skipAuthRefresh) return true;
	const path = requestPath(config);
	return SKIP_AUTH_RETRY_PATHS.some((p) => path.includes(p));
}

function sleep(ms) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

async function withStorageLock(run) {
	if (typeof localStorage === 'undefined') {
		return run();
	}

	const owner = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
	const started = Date.now();

	while (Date.now() - started < LOCK_TTL_MS) {
		let lock = null;
		try {
			lock = JSON.parse(localStorage.getItem(LOCK_STORAGE_KEY) || 'null');
		} catch {
			lock = null;
		}
		const expired = !lock || !lock.until || lock.until < Date.now();

		if (expired) {
			localStorage.setItem(
				LOCK_STORAGE_KEY,
				JSON.stringify({ owner, until: Date.now() + LOCK_TTL_MS }),
			);
			await sleep(20);
			let confirmed = null;
			try {
				confirmed = JSON.parse(localStorage.getItem(LOCK_STORAGE_KEY) || 'null');
			} catch {
				confirmed = null;
			}
			if (confirmed?.owner === owner) {
				try {
					return await run();
				} finally {
					let current = null;
					try {
						current = JSON.parse(localStorage.getItem(LOCK_STORAGE_KEY) || 'null');
					} catch {
						current = null;
					}
					if (current?.owner === owner) {
						localStorage.removeItem(LOCK_STORAGE_KEY);
					}
				}
			}
		}

		await sleep(50);
	}

	return run();
}

async function withRefreshLock(run) {
	if (typeof navigator !== 'undefined' && navigator.locks?.request) {
		return navigator.locks.request('grizzly-tg-auth-refresh', run);
	}
	return withStorageLock(run);
}

/**
 * Attach JWT refresh / migrate to the telegram mini-app axios agent.
 * Mirrors sms-front attachAuthRefresh, storing tokens in localStorage.
 */
export function attachAuthRefresh(agent, { onSessionExpired } = {}) {
	if (!agent || agent.__authRefreshAttached) {
		return agent;
	}

	agent.__authRefreshAttached = true;
	hydrateAuthFromStorage(agent);

	let refreshPromise = null;
	let migratePromise = null;
	let migrateDisabled = false;

	const applyTokens = (payload) => persistAuthSession({ agent, payload });

	const clearSession = () => {
		clearAuthSession({ agent });
		if (typeof onSessionExpired === 'function') {
			onSessionExpired();
		}
	};

	const postRefresh = async (refreshToken) => {
		const { data } = await agent.post(
			REFRESH_PATH,
			{ refresh_token: refreshToken },
			{ __skipAuthRefresh: true },
		);
		return applyTokens(data);
	};

	const refreshTokens = () => {
		if (refreshPromise) return refreshPromise;

		refreshPromise = withRefreshLock(async () => {
			const session = readAuthSession();
			if (session.accessToken && !isAccessTokenExpiringSoon(session.accessExpiresAt)) {
				persistAuthSession({
					agent,
					payload: {
						access_token: session.accessToken,
						refresh_token: session.refreshToken,
						access_token_expires_at: session.accessExpiresAt,
					},
				});
				return {
					accessToken: session.accessToken,
					refreshToken: session.refreshToken,
					accessExpiresAt: session.accessExpiresAt,
				};
			}

			const refreshToken = session.refreshToken;
			if (!refreshToken) {
				const error = new Error('Missing refresh token');
				error.response = { status: 401 };
				throw error;
			}

			try {
				return await postRefresh(refreshToken);
			} catch (error) {
				if (error?.response?.status === 401) {
					clearSession();
				}
				throw error;
			}
		}).finally(() => {
			refreshPromise = null;
		});

		return refreshPromise;
	};

	const migrateLegacyToken = () => {
		if (migrateDisabled) return Promise.resolve(null);
		if (migratePromise) return migratePromise;

		migratePromise = (async () => {
			const session = readAuthSession();
			if (!session.accessToken || session.refreshToken) {
				return null;
			}

			try {
				const { data } = await agent.post(
					MIGRATE_PATH,
					{},
					{ __skipAuthRefresh: true },
				);
				return applyTokens(data);
			} catch (error) {
				if (error?.response?.status === 401) {
					clearSession();
				} else {
					// Endpoint missing / temporary error — do not spam migrate on every request.
					migrateDisabled = true;
				}
				throw error;
			}
		})().finally(() => {
			migratePromise = null;
		});

		return migratePromise;
	};

	/**
	 * Re-auth via Telegram initData when refresh pair is unavailable / failed.
	 */
	const reauthWithTelegram = async () => {
		const chatId = window?.Telegram?.WebApp?.initDataUnsafe?.user?.id;
		const params = window?.Telegram?.WebApp?.initData;
		if (!chatId || !params) {
			const error = new Error('Missing Telegram initData');
			error.response = { status: 401 };
			throw error;
		}

		const { data } = await agent.post(
			'/api/tg-user-auth',
			{ 'chat-id': chatId, params },
			{ __skipAuthRefresh: true },
		);
		return applyTokens(data);
	};

	agent.refreshAuth = refreshTokens;
	agent.migrateAuth = migrateLegacyToken;
	agent.reauthWithTelegram = reauthWithTelegram;
	agent.applyAuthTokens = applyTokens;
	agent.clearAuth = clearSession;

	agent.interceptors.request.use(async (config) => {
		if (shouldSkipAuthRetry(config)) {
			return config;
		}

		const session = readAuthSession();
		if (session.accessToken && !session.refreshToken) {
			try {
				await migrateLegacyToken();
			} catch {
				// fall through with legacy token
			}
		}

		const latest = readAuthSession();
		const needsRefresh =
			Boolean(latest.refreshToken) &&
			(!latest.accessToken ||
				isAccessTokenExpiringSoon(latest.accessExpiresAt || agent.accessExpiresAt));

		if (needsRefresh) {
			try {
				await refreshTokens();
			} catch {
				// response interceptor may still recover / clear
			}
		}

		const accessToken = readAuthSession().accessToken;
		if (accessToken) {
			config.headers = config.headers || {};
			config.headers.Authorization = `Bearer ${accessToken}`;
		}

		return config;
	});

	agent.interceptors.response.use(
		(response) => response,
		async (error) => {
			const { config, response } = error || {};
			if (!config || response?.status !== 401 || shouldSkipAuthRetry(config)) {
				return Promise.reject(error);
			}

			const session = readAuthSession();

			if (!session.refreshToken && session.accessToken) {
				try {
					await migrateLegacyToken();
					config.__authRetry = true;
					const accessToken = readAuthSession().accessToken;
					if (accessToken) {
						config.headers = config.headers || {};
						config.headers.Authorization = `Bearer ${accessToken}`;
					}
					return agent.request(config);
				} catch {
					// try Telegram re-auth below
				}
			}

			if (session.refreshToken) {
				try {
					await refreshTokens();
					config.__authRetry = true;
					const accessToken = readAuthSession().accessToken;
					if (accessToken) {
						config.headers = config.headers || {};
						config.headers.Authorization = `Bearer ${accessToken}`;
					}
					return agent.request(config);
				} catch (refreshError) {
					// fall through to Telegram re-auth
					if (!window?.Telegram?.WebApp?.initData) {
						return Promise.reject(refreshError);
					}
				}
			}

			try {
				await reauthWithTelegram();
				config.__authRetry = true;
				const accessToken = readAuthSession().accessToken;
				if (accessToken) {
					config.headers = config.headers || {};
					config.headers.Authorization = `Bearer ${accessToken}`;
				}
				return agent.request(config);
			} catch (reauthError) {
				clearSession();
				return Promise.reject(reauthError);
			}
		},
	);

	const bootSession = readAuthSession();
	if (bootSession.accessToken && !bootSession.refreshToken) {
		migrateLegacyToken().catch(() => {});
	}

	return agent;
}
